// 이용권 내역 페이지

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  Stack,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';  // getSession 용

// 자녀 이용권 정보 타입
interface ChildMembership {
  id: string;
  name: string;
  organizationName: string;
  membershipType: string | null;
  remainingTime: number | null; // 분 단위
  expiryDate: string | null;
  ticketName: string | null;
  ticketStartDate: string | null;
  ticketEndDate: string | null;
}

export default function PaymentPage() {
  const { parent } = useSupabaseParentAuth();

  const [memberships, setMemberships] = useState<ChildMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (parent?.id) {
      loadMemberships();
    }
  }, [parent?.id]);

  const loadMemberships = async () => {
    if (!parent?.id) return;

    setIsLoading(true);
    setError('');

    try {
      // 백엔드 API를 통해 자녀 목록 조회
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      // 1. 자녀 목록 조회
      const childrenResponse = await fetch('/api/v1/parent/children', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!childrenResponse.ok) {
        throw new Error('자녀 목록 조회 실패');
      }

      const children = await childrenResponse.json();

      if (!children || children.length === 0) {
        setMemberships([]);
        return;
      }

      // 2. API 응답에서 이용권 정보 직접 사용 (백엔드에서 조회)
      const membershipData: ChildMembership[] = children.map((child: any) => {
        return {
          id: child.id,
          name: child.name || '이름 없음',
          organizationName: child.organization_name || '독서실',
          membershipType: child.membership_type || null,
          remainingTime: child.remaining_time ?? null,
          expiryDate: child.expiry_date || null,
          ticketName: null,
          ticketStartDate: null,
          ticketEndDate: null,
        };
      });

      setMemberships(membershipData);
    } catch (err: any) {
      console.error('이용권 조회 오류:', err);
      setError(err.message || '이용권 정보를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatRemainingTime = (minutes: number | null) => {
    if (minutes === null || minutes === undefined) return '-';

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours}시간 ${mins}분`;
    } else if (hours > 0) {
      return `${hours}시간`;
    } else {
      return `${mins}분`;
    }
  };

  const getMembershipStatus = (membership: ChildMembership) => {
    // 이용권 정보가 있는지 확인 (membership_type, expiry_date, remaining_time 중 하나라도 있으면)
    const hasMembership = membership.membershipType || membership.ticketName ||
      membership.expiryDate || (membership.remainingTime !== null && membership.remainingTime > 0);

    if (!hasMembership) {
      return { label: '이용권 없음', color: 'default' as const };
    }

    if (membership.expiryDate) {
      const expiry = new Date(membership.expiryDate);
      const now = new Date();
      const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysLeft < 0) {
        return { label: '만료됨', color: 'error' as const };
      } else if (daysLeft <= 7) {
        return { label: `${daysLeft}일 남음`, color: 'warning' as const };
      } else {
        return { label: '사용중', color: 'success' as const };
      }
    }

    if (membership.remainingTime !== null && membership.remainingTime <= 0) {
      return { label: '소진됨', color: 'error' as const };
    }

    return { label: '사용중', color: 'success' as const };
  };

  const getDaysProgress = (startDate: string | null, endDate: string | null) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const now = new Date().getTime();

    if (now >= end) return 100;
    if (now <= start) return 0;

    return Math.round(((now - start) / (end - start)) * 100);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        이용권 내역
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        자녀의 이용권 현황을 확인할 수 있습니다.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {memberships.length === 0 ? (
        <Alert severity="info">
          등록된 자녀가 없습니다.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {memberships.map((membership) => {
            const status = getMembershipStatus(membership);
            const progress = getDaysProgress(membership.ticketStartDate, membership.ticketEndDate || membership.expiryDate);

            return (
              <Box key={membership.id} sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}>
                <Card elevation={2}>
                  <CardContent>
                    {/* 헤더: 자녀 이름과 상태 */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                          {membership.name}
                        </Typography>
                      </Box>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {membership.organizationName}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* 이용권 정보 */}
                    {(membership.membershipType || membership.ticketName || membership.expiryDate || (membership.remainingTime !== null && membership.remainingTime > 0)) ? (
                      <>
                        {/* 이용권 종류 (있는 경우만 표시) */}
                        {(membership.membershipType || membership.ticketName) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              이용권 종류
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {membership.ticketName || membership.membershipType}
                            </Typography>
                          </Box>
                        )}

                        {/* 남은 시간 (시간권인 경우) */}
                        {membership.remainingTime !== null && membership.remainingTime > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <AccessTimeIcon fontSize="small" color="action" />
                              <Typography variant="subtitle2" color="text.secondary">
                                남은 시간
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="primary">
                              {formatRemainingTime(membership.remainingTime)}
                            </Typography>
                          </Box>
                        )}

                        {/* 만료일 / 사용 기간 */}
                        {(membership.expiryDate || membership.ticketEndDate) && (
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <CalendarTodayIcon fontSize="small" color="action" />
                              <Typography variant="subtitle2" color="text.secondary">
                                {membership.ticketStartDate ? '사용 기간' : '만료일'}
                              </Typography>
                            </Box>
                            {membership.ticketStartDate ? (
                              <Typography variant="body1">
                                {formatDate(membership.ticketStartDate)} ~ {formatDate(membership.ticketEndDate || membership.expiryDate)}
                              </Typography>
                            ) : (
                              <Typography variant="body1" fontWeight={500}>
                                {formatDate(membership.expiryDate)}
                              </Typography>
                            )}

                            {/* 진행률 바 */}
                            {progress > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={progress}
                                  color={progress > 80 ? 'warning' : 'primary'}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                  {progress}% 경과
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{ py: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          등록된 이용권이 없습니다.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          독서실에 문의하여 이용권을 구매해주세요.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
